import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Home, FolderOpen, Crown, Settings, Play, Gift } from 'lucide-react';
import { HelpPopup } from '@/components/shared/HelpPopup';
import { useUserStore } from '@/store/userStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { canSeeRewards } from '@/utils/rewardsHelpers';

export function Sidebar() {
  const { t } = useTranslation();
  const [showHelpPopup, setShowHelpPopup] = useState(false);
  const { user } = useUserStore();
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId);
  const showRewards = canSeeRewards(user, activeOrgId);

  type NavItem = {
    path: string;
    icon: typeof Home;
    label: string;
    isUpgrade?: boolean;
    isReward?: boolean;
  };
  const navItems: NavItem[] = [
    { path: '/', icon: Home, label: t('navigation.home') },
    { path: '/files', icon: FolderOpen, label: t('navigation.files') },
    { path: '/examples', icon: Play, label: t('navigation.examples') },
    { path: '/subscription', icon: Crown, label: t('navigation.upgrade'), isUpgrade: true },
    ...(showRewards
      ? [{ path: '/rewards', icon: Gift, label: t('navigation.rewards'), isReward: true } as NavItem]
      : []),
    { path: '/account', icon: Settings, label: t('navigation.account') },
  ];

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-surface/90 backdrop-blur-xl border-r border-glass-border flex flex-col z-40">
      {/* Logo */}
      <div className="p-6 flex items-center gap-3">
        <div className="relative">
        <img src="/logo.svg" alt="Vivideo" className="w-10 h-10 rounded-xl" />
          <div className="absolute inset-0 rounded-xl bg-primary/20 blur-md -z-10" />
        </div>
        <span className="text-xl font-bold text-text-primary">{t('appName')}</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4">
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300',
                    'hover:bg-surface-light group',
                    isActive
                      ? 'bg-gradient-to-r from-primary/20 to-primary/10 text-primary border border-primary/30 shadow-[0_0_15px_rgba(139,92,246,0.15)]'
                      : 'text-text-secondary hover:text-text-primary border border-transparent'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={cn(
                      "w-5 h-5 transition-transform group-hover:scale-110",
                      item.isUpgrade && !isActive && "text-orange-500"
                    )} />
                    <span className="font-medium">{item.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom section */}
      <div className="p-4 border-t border-glass-border">
        <button
          onClick={() => setShowHelpPopup(true)}
          className="w-full text-left px-4 py-3 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 backdrop-blur-sm hover:from-primary/15 hover:to-accent/15 transition-colors"
        >
          <p className="text-xs text-text-secondary">{t('support.help')}?</p>
          <span className="text-sm text-primary">{t('support.title')}</span>
        </button>
      </div>

      <HelpPopup open={showHelpPopup} onClose={() => setShowHelpPopup(false)} />
    </aside>
  );
}
